import PropTypes from "prop-types";
import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";

import { useTheme } from "@mui/material/styles";
import { Box, Divider, Typography, useMediaQuery } from "@mui/material";
import MuiBreadcrumbs from "@mui/material/Breadcrumbs";
import { gridSpacing } from "store/constant";
import { IconTallymark1 } from "@tabler/icons-react";
import { LocusHomeIcon } from "assets/icons/LocusIcons.js";
import { SitePopover } from "./SitePopover";
import { useSelector, useDispatch } from "store";
import { linkSX } from "./Breadcrumb.helpers";
import { activeRoutes } from "../../routes";
import { getSitesAvailableToUser } from "../../routes/ProtectedRoute";
import { getClientByDomain } from "../../hooks/contexts/AuthContext.helpers";
import { setSelectedClient } from "../../store/slices/customer";
import { useLocusRouter } from "../../hooks/useLocusRouter";
import testLogoDark from "../../assets/images/Test-Logo_Dark.svg";
import testLogoLight from "../../assets/images/Test-Logo_Light.svg";
import { storage } from "../../utils/helpers/storage";

export const Breadcrumbs = ({
  card,
  divider,
  icon,
  icons,
  maxItems,
  separator,
}) => {
  const theme = useTheme();
  const { selectSite, getHomeRoute } = useLocusRouter();
  const { selectedClient } = useSelector((state) => state.customer);
  const dispatch = useDispatch();
  const { selectedSite } = useSelector((state) => state.site);
  const { selectedDashboard } = useSelector((state) => state.analytics);
  const { viewState, activeUser } = useSelector((state) => state.user);
  const { kioskMode, kioskHideBranding } = useSelector((state) => state.menu);
  const { getBreadcrumbRoute } = useLocusRouter();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm")); // Breakpoint for tablet or mobile
  const isTablet = useMediaQuery(theme.breakpoints.between("sm", "md")); // Tablet only

  const iconStyle = {
    marginRight: theme.spacing(0.75),
    marginTop: `-${theme.spacing(0.25)}`,
    width: "24px",
    height: "24px",
    color: theme.palette.primary.main,
  };

  const [search, setSearch] = useState("");

  // When sites are string IDs (user lacks Cross-Site Viewer), re-fetch client
  // with showSiteData=true to get full site objects for the dropdown.
  useEffect(() => {
    if (
      selectedClient?.id &&
      selectedClient?.sites?.length > 0 &&
      typeof selectedClient.sites[0] === "string"
    ) {
      let cancelled = false;
      getClientByDomain(storage.getDomain(), { showSiteData: true }).then(
        (client) => {
          if (!cancelled && client?.sites) {
            dispatch(setSelectedClient(client));
          }
        },
      );
      return () => { cancelled = true; };
    }
  }, [selectedClient?.id, selectedClient?.sites, dispatch]);

  const location = useLocation().pathname.split("/");
  location.shift();

  const sites = getSitesAvailableToUser({
    sites: selectedClient?.sites,
    groups: activeUser?.groups,
  });

  const filterSites = !!search
    ? sites?.filter((site) =>
        site.friendlyName.toLowerCase().includes(search?.toLowerCase() || ""),
      )
    : sites;

  const handleSearchSites = async (event) => {
    setSearch(event?.target?.value || event);
  };

  const isPerformanceSection = location.some((path) =>
    ["performance", "visibility", "management"].includes(path.toLowerCase()),
  );

  // item separator
  const SeparatorIcon = separator;
  const separatorIcon = separator ? (
    <SeparatorIcon stroke={1.5} size="16px" />
  ) : (
    <IconTallymark1 stroke={1.5} size="16px" />
  );

  let ItemIcon;

  const showClient = viewState === "client" || viewState === "site";
  const showDashboardSelector =
    viewState === "site" &&
    (location.includes("dashboards") || location.includes("reports"));

  let getRoute = (path, routes) => {
    return routes?.filter((route) => {
      return route.path === path;
    })[0];
  };
  const locationMap = useMemo(() => {
    let parentPath =
      location[0] === "client" ? `/client/:clientId` : `/${location[0]}`;
    let rootRoute = getRoute(parentPath, activeRoutes);

    let _locationMap = [];
    // handle global overview breadcrumb for '/locus' since global overview shouldn't be prefixed to every locus child route
    if (location.length === 1 && location[0] === "locus") {
      _locationMap.push(getRoute("", rootRoute?.children));
    }
    location.reduce((acc, path) => {
      if (path === "site") {
        path = "site/:siteId";
      }

      let childNav = getRoute(path, acc?.children);
      if (childNav) {
        _locationMap.push(childNav);
        acc = childNav;
      }
      return acc;
    }, rootRoute);
    return _locationMap;
  }, [location, activeRoutes]);

  const mainContent = useMemo(() => {
    let fullRoute = "";
    // eslint-disable-next-line array-callback-return
    return locationMap?.map((route) => {
      // Hold reference to parent
      fullRoute += route.path + "/";
      // ItemIcon = navItem?.icon ? navItem.icon : AccountTreeTwoToneIcon;
      if (route?.breadcrumb?.display) {
        return (
          <Typography
            key={route.path}
            component={Link}
            to={getBreadcrumbRoute(fullRoute)}
            // to={getNavItemURL({ location, navItem, url: navItem?.url })}
            variant="subtitle1"
            sx={{ ...linkSX(theme), fontSize: isTablet ? 13 : 14 }}
          >
            {icons && (
              <ItemIcon
                style={iconStyle}
                sx={{ color: theme.palette.primary.main }}
              />
            )}
            {route.breadcrumb?.display}
          </Typography>
        );
      }
    });
  }, [locationMap, isTablet]);

  return (
    <>
      <Box>
        <MuiBreadcrumbs
          sx={{
            "& .MuiBreadcrumbs-separator":
              isMobile && isPerformanceSection
                ? { display: "none" }
                : { width: 16, ml: isTablet ? 0.5 : 1.25, mr: isTablet ? 0.5 : 1.25 },
          }}
          aria-label="breadcrumb"
          maxItems={maxItems || 8}
          separator={isMobile && isPerformanceSection ? null : separatorIcon} // Hide separator on mobile or performance sections
        >
          {/* Hide home icon on mobile */}
          {!!activeUser?.groups?.length && !isMobile && (
            <Typography
              component={Link}
              to={getHomeRoute()}
              color="inherit"
              variant="subtitle1"
              sx={{ ...linkSX(theme), fontSize: isTablet ? 13 : 14 }}
              aria-label="home"
            >
              {icons && (
                <LocusHomeIcon sx={{ color: theme.palette.primary.main }} />
              )}
              {showClient && !kioskHideBranding && !isMobile ? (
                <Box
                  style={{
                    height: "inherit",
                    maxWidth: "100%",
                    maxHeight: "100%",
                    margin: "auto",
                    display: "flex",
                  }}
                >
                  <img
                    src={
                      activeUser.theme === "dark"
                        ? selectedClient?.darkLogoUrl || testLogoDark
                        : selectedClient?.lightLogoUrl || testLogoLight
                    }
                    alt="Logo"
                    height="auto"
                    style={{
                      maxHeight: "100%",
                      maxWidth: isTablet ? "120px" : "150px",
                      objectFit: "contain",
                      height: isTablet ? "32px" : "40px",
                      width: "auto",
                    }}
                  />
                </Box>
              ) : (
                <LocusHomeIcon
                  sx={{ mr: 0, color: theme.palette.primary.main }}
                />
              )}
              {!icon && "Dashboard"}
            </Typography>
          )}

          {showClient && !kioskMode && !kioskHideBranding && (
            <SitePopover
              handleSearchSites={handleSearchSites}
              handleSelectSite={selectSite}
              filterSites={filterSites}
            />
          )}

          {/* MOBILE: Replace dashboards/reports breadcrumb with dashboard/report name */}
          {isMobile &&
            !kioskMode &&
            (() => {
              // Site-level: /client/:id/site/:id/dashboards|reports → index 2
              // Locus-level: /locus/reports → index 1
              const isDashboards = location.includes("dashboards");
              const isReports = location.includes("reports");
              const reportsIdx = isDashboards
                ? location.indexOf("dashboards")
                : location.indexOf("reports");
              const isBase = reportsIdx >= 0 && reportsIdx === location.length - 1;
              const isSub = reportsIdx >= 0 && location.length > reportsIdx + 1;
              // If on /dashboards or /reports base, show label
              if ((isDashboards || isReports) && isBase) {
                return (
                  <Typography
                    color="inherit"
                    variant="subtitle1"
                    sx={linkSX(theme)}
                  >
                    {isDashboards ? "Dashboards" : "Reports"}
                  </Typography>
                );
              }
              // If on dashboards/reports sub-route, show dashboard/report name if available
              if ((isDashboards || isReports) && isSub) {
                return (
                  <Typography
                    color="inherit"
                    variant="subtitle1"
                    sx={linkSX(theme)}
                  >
                    {selectedDashboard?.name || ""}
                  </Typography>
                );
              }
              return mainContent;
            })()}

          {/* DESKTOP: Show normal breadcrumbs */}
          {!isMobile && !kioskMode && mainContent}

          {(showDashboardSelector ||
            (viewState === "locus" && location.includes("reports"))) &&
            !isMobile &&
            !kioskMode &&
            location.some((path) =>
              ["performance", "visibility", "management"].includes(
                path.toLowerCase(),
              ),
            ) && (
              <Typography color="inherit" variant="body1" sx={{ ...linkSX(theme), fontSize: isTablet ? 13 : 14 }}>
                {selectedDashboard?.name}
              </Typography>
            )}

          {storage.getKioskId() && (
            <Typography color="inherit" variant="body2" sx={linkSX(theme)}>
              {selectedDashboard?.name}
            </Typography>
          )}

          {kioskMode &&
            !isMobile &&
            selectedSite?.friendlyName &&
            !kioskHideBranding && (
              <Typography color="inherit" variant="body1" sx={{ ...linkSX(theme), fontSize: isTablet ? 13 : 14 }}>
                {selectedSite?.friendlyName}
              </Typography>
            )}

          {kioskMode &&
            !isMobile &&
            (storage.getKioskName() || selectedDashboard.name) && (
              <Typography
                color="inherit"
                variant="body1"
                sx={{
                  ...linkSX(theme),
                  fontSize: storage.getKioskName() ? "32px" : "14px",
                  fontWeight: storage.getKioskName() ? "600" : "400",
                }}
              >
                {storage.getKioskName() || selectedDashboard.name}
              </Typography>
            )}
        </MuiBreadcrumbs>
      </Box>
      {card === false && divider !== false && (
        <Divider
          sx={{ borderColor: theme.palette.primary.main, mb: gridSpacing }}
        />
      )}
    </>
  );
};

Breadcrumbs.propTypes = {
  card: PropTypes.bool,
  divider: PropTypes.bool,
  icon: PropTypes.bool,
  icons: PropTypes.bool,
  maxItems: PropTypes.number,
  navigation: PropTypes.object,
  separator: PropTypes.oneOfType([PropTypes.func, PropTypes.object]),
};
